import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import { BiExport } from "react-icons/bi";
import { CiSearch } from "react-icons/ci";
import { TailSpin } from "react-loader-spinner";

import { DataTable } from "../../../components/data-table";
import Navbar from "../../../components/Navbar";
import Sidebar from "../../../components/Sidebar";
import { PROD_LINK, STAGING_LINK } from "../../../constant";
import { IS_STAGING } from "../../../constant/api";
import { useAuth } from "../../../context/auth-context";
import {
  isValidCustomRange,
  type IsoDateRange,
} from "../../../lib/adminExport/datePresets";
import {
  type SignupKycStatusFilter,
  useAdminSignupsDataQuery,
  useAdminSignupsSummaryQuery,
} from "../../../lib/api";
import { formatOrdinalDateTime } from "../clientsTable";
import CustomerModal from "../CustomerModal";
import { customerKycLevel, type CustomerRecord } from "../customersTable";
import { fetchSignupsForExport } from "./fetchSignupsExport";
import SignupsChart from "./SignupsChart";
import SignupsStats from "./SignupsStats";
import {
  describeRange,
  getSignupPresetRange,
  SIGNUP_RANGE_PRESETS,
  type SignupRangePreset,
} from "./signupDateRanges";
import {
  buildSignupRows,
  buildSignupsCsv,
  createSignupColumns,
  SIGNUPS_TABLE_LIMIT,
  type SignupRow,
} from "./signupsTable";

const KYC_TABS: { id: SignupKycStatusFilter; label: string }[] = [
  { id: "", label: "All" },
  { id: "VERIFIED", label: "Verified" },
  { id: "DENIED", label: "Denied" },
  { id: "FAILED", label: "Failed" },
];

function errMessage(err: unknown, fallback: string): string {
  const m = (err as { response?: { data?: { message?: unknown } } })?.response
    ?.data?.message;
  if (typeof m === "string") return m;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

const chipClass = (active: boolean) =>
  `rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition ${
    active
      ? "border-secondary_button bg-secondary_button text-white"
      : "border-[#2C363E] bg-[#1C2227] text-[#869AA9] hover:border-[#3A4852] hover:text-[#C6D0D7]"
  }`;

const dateInputClass =
  "h-10 rounded-xl border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none [color-scheme:dark] focus:border-secondary_button";

const NewSignupsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { setCustomerId, setUserActivities } = useAuth();

  const [preset, setPreset] = useState<SignupRangePreset>("last30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [kycStatus, setKycStatus] = useState<SignupKycStatusFilter>("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const [customerModal, setCustomerModal] = useState(false);
  const [modalDate, setModalDate] = useState("");
  const [modalKycLevel, setModalKycLevel] = useState("");

  /** null while a custom range is half-filled; queries keep the last range. */
  const pendingRange: IsoDateRange | null = useMemo(() => {
    if (preset !== "custom") return getSignupPresetRange(preset);
    return isValidCustomRange(customStart, customEnd)
      ? { startDate: customStart, endDate: customEnd }
      : null;
  }, [preset, customStart, customEnd]);

  const [range, setRange] = useState<IsoDateRange>(() =>
    getSignupPresetRange("last30d")
  );
  useEffect(() => {
    if (
      pendingRange &&
      (pendingRange.startDate !== range.startDate ||
        pendingRange.endDate !== range.endDate)
    ) {
      setRange(pendingRange);
    }
  }, [pendingRange, range]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [range, search, kycStatus]);

  const summaryQuery = useAdminSignupsSummaryQuery(range);
  const dataQuery = useAdminSignupsDataQuery({
    page,
    limit: SIGNUPS_TABLE_LIMIT,
    range,
    search,
    kycStatus,
  });

  const users = dataQuery.data?.users;
  const pagination = dataQuery.data?.pagination;
  const rows = useMemo(() => buildSignupRows(users ?? []), [users]);

  const openUser = useCallback(
    (row: SignupRow) => {
      setCustomerId(row.raw._id);
      setModalDate(formatOrdinalDateTime(row.raw.lastLogin));
      setModalKycLevel(customerKycLevel(row.raw as CustomerRecord));
      setCustomerModal(true);
    },
    [setCustomerId]
  );

  const columns = useMemo(() => createSignupColumns(openUser), [openUser]);

  /** "See user activities" lives on the Users page, so hop over there. */
  const openActivities = useCallback(
    (value: boolean) => {
      setUserActivities(value);
      if (value) navigate("/users");
    },
    [navigate, setUserActivities]
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const all = await fetchSignupsForExport({ range, search, kycStatus });
      if (all.length === 0) {
        toast.error("No signups to export for these filters.");
        return;
      }
      const csv = buildSignupsCsv(all);
      const suffix = kycStatus ? `_${kycStatus.toLowerCase()}` : "";
      saveAs(
        new Blob([csv], { type: "text/csv;charset=utf-8" }),
        `new-signups_${range.startDate}_to_${range.endDate}${suffix}.csv`
      );
      toast.success(`Exported ${all.length.toLocaleString()} signups`);
    } catch (e) {
      toast.error(errMessage(e, "Could not export signups."), {
        duration: 4000,
      });
    } finally {
      setExporting(false);
    }
  }, [range, search, kycStatus]);

  const customInvalid =
    preset === "custom" &&
    Boolean(customStart && customEnd) &&
    !isValidCustomRange(customStart, customEnd);

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden">
      <div className="flex h-[31px] w-full shrink-0 items-center justify-center bg-[#1B1B1B]">
        {IS_STAGING ? (
          <h4 className="text-[12px] font-medium text-[#AEBCC6]">
            You are in Staging:{" "}
            <a href={PROD_LINK} className="text-[#03C668]">
              Switch to Prod
            </a>
          </h4>
        ) : (
          <h4 className="text-[12px] font-medium text-[#AEBCC6]">
            You are in PROD:{" "}
            <a href={STAGING_LINK} className="text-[#DF4E4E]">
              Switch to Staging
            </a>
          </h4>
        )}
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden lgss:flex-row">
        <Sidebar />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-[#13181B] lgss:w-10/12">
          <Navbar />
          <div className="mx-auto w-full max-w-[1600px] flex-1 px-5 pb-24 pt-6 font-manrope mds:px-8 lgss:px-10">
            <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="font-sora text-xl font-semibold tracking-tight text-white">
                  New Signups
                </h1>
                <p className="mt-1 text-[13px] leading-relaxed text-[#869AA9]">
                  {describeRange(range)}
                </p>
              </div>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-secondary_button px-4 text-[13px] font-semibold text-white shadow-[0_8px_24px_rgba(11,132,203,0.35)] transition hover:brightness-110 disabled:cursor-wait disabled:opacity-70"
              >
                {exporting ? (
                  <TailSpin color="#fff" height={16} width={16} />
                ) : (
                  <BiExport className="text-base" />
                )}
                {exporting ? "Exporting…" : "Export CSV"}
              </button>
            </header>

            <div className="mb-6">
              <div
                className="flex flex-wrap gap-2"
                role="group"
                aria-label="Date range"
              >
                {SIGNUP_RANGE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={preset === p.id}
                    onClick={() => setPreset(p.id)}
                    className={chipClass(preset === p.id)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {preset === "custom" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <label className="sr-only" htmlFor="signups-start">
                    Start date
                  </label>
                  <input
                    id="signups-start"
                    type="date"
                    value={customStart}
                    max={customEnd || undefined}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className={dateInputClass}
                  />
                  <span className="text-[13px] text-[#869AA9]">to</span>
                  <label className="sr-only" htmlFor="signups-end">
                    End date
                  </label>
                  <input
                    id="signups-end"
                    type="date"
                    value={customEnd}
                    min={customStart || undefined}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className={dateInputClass}
                  />
                  {customInvalid && (
                    <span className="text-[12px] text-red-300">
                      Start date must be before end date.
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-6">
              {summaryQuery.isError && (
                <div className="flex flex-col gap-2 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
                  <span>
                    {errMessage(
                      summaryQuery.error,
                      "Could not load the summary."
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => summaryQuery.refetch()}
                    className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
                  >
                    Retry
                  </button>
                </div>
              )}

              <SignupsStats
                summary={summaryQuery.data}
                isLoading={summaryQuery.isPending}
              />
              <SignupsChart
                daily={summaryQuery.data?.daily}
                isLoading={summaryQuery.isFetching}
              />

              <section className="rounded-2xl border border-[#2C363E] bg-[#161E25]/40 p-4 sm:p-5">
                <div className="mb-4">
                  <h2 className="font-sora text-[16px] font-semibold text-white">
                    Signups
                  </h2>
                  <p className="mt-1 text-[13px] text-[#869AA9]">
                    {pagination
                      ? `${pagination.totalCount.toLocaleString()} account${
                          pagination.totalCount === 1 ? "" : "s"
                        } match these filters.`
                      : "Everyone who created an account in this range."}
                  </p>
                </div>

                <div className="mb-3 flex h-11 items-center gap-2 rounded-xl border border-[#2C363E] bg-[#13181B] px-3">
                  <CiSearch className="shrink-0 text-[22px] text-[#869AA9]" />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by name or email..."
                    aria-label="Search signups"
                    className="w-full bg-transparent text-[13px] text-[#E7EBEE] outline-none placeholder:text-[#5C6B76]"
                  />
                </div>

                <div
                  className="mb-4 flex flex-wrap items-center gap-2"
                  role="group"
                  aria-label="KYC status"
                >
                  <span className="mr-1 text-[12px] font-medium text-[#869AA9]">
                    KYC
                  </span>
                  {KYC_TABS.map((tab) => (
                    <button
                      key={tab.id || "all"}
                      type="button"
                      aria-pressed={kycStatus === tab.id}
                      onClick={() => setKycStatus(tab.id)}
                      className={chipClass(kycStatus === tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {dataQuery.isFetching && !dataQuery.isPending && (
                  <p className="mb-2 text-[12px] text-[#869AA9]">
                    Updating list…
                  </p>
                )}

                {dataQuery.isError && (
                  <div className="mb-4 flex flex-col gap-2 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                      {errMessage(dataQuery.error, "Could not load signups.")}
                    </span>
                    <button
                      type="button"
                      onClick={() => dataQuery.refetch()}
                      className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {dataQuery.isPending && !dataQuery.isError && (
                  <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
                    <TailSpin color="#0B75C2" height={36} width={36} />
                  </div>
                )}

                {!dataQuery.isPending && !dataQuery.isError && (
                  <DataTable<SignupRow>
                    columns={columns}
                    data={rows}
                    currentPage={page}
                    setCurrentPage={setPage}
                    totalPage={Math.max(1, pagination?.totalPages ?? 1)}
                    count={pagination?.totalCount ?? rows.length}
                    limit={SIGNUPS_TABLE_LIMIT}
                    onRowClick={openUser}
                    emptyContent="No signups match these filters."
                    className="overflow-hidden rounded-xl border border-[#2C363E]"
                    fixedBodyHeight="min(560px, 55vh)"
                  />
                )}
              </section>
            </div>
          </div>
        </div>
      </div>

      {customerModal ? (
        <CustomerModal
          date={modalDate}
          kycLevel={modalKycLevel}
          setUserActivities={openActivities}
          setCustomerModal={setCustomerModal}
        />
      ) : null}
    </div>
  );
};

export default NewSignupsOverview;
